import { useMemo } from 'react';
import { IntlMessageFormat } from 'intl-messageformat';
import { useI18n } from '@xdocs/ui';
import type { TranslateVars } from '@xdocs/i18n';
import type { WebCatalogKey } from './webCatalog.js';
import { WEB_CATALOG } from './webCatalog.js';

export type WebMessageId = `web:${WebCatalogKey}`;

const PREFIX_LENGTH = 'web:'.length;

/**
 * `t()` for the stop-gap `web:` scope (see `webCatalog.ts`). Shares
 * `useI18n()`'s current locale so it stays in lockstep with the rest of the
 * app (LanguageSwitcher, `@xdocs/ui` copy) — never a second locale source.
 */
export function useWebT(): { t: (id: WebMessageId, vars?: TranslateVars) => string } {
  const { locale } = useI18n();

  const t = useMemo(() => {
    return (id: WebMessageId, vars?: TranslateVars): string => {
      // Safe: `id`'s type guarantees the slice is a valid `WebCatalogKey` —
      // `.slice()` itself only ever returns a widened `string` at the type
      // level (template-literal types don't survive string ops), hence the cast.
      const key = id.slice(PREFIX_LENGTH) as WebCatalogKey;
      const raw = WEB_CATALOG[locale][key] ?? WEB_CATALOG.en[key];
      if (!vars) return raw;
      const formatted = new IntlMessageFormat(raw, locale).format(vars);
      if (formatted == null) return '';
      if (typeof formatted === 'string') return formatted;
      return Array.isArray(formatted) ? formatted.map(String).join('') : String(formatted);
    };
  }, [locale]);

  return { t };
}
